import React, { useState, useEffect } from "react"; // Import useState and useEffect
import { useNavigate, useLocation } from "react-router-dom"; // Import useNavigate
import paths from "routes/paths";
import IconButton from "components/common/iconButton";
import {
  Card,
  Breadcrumb,
  Col,
  Row,
  Table,
  Spinner,
  Modal,
  Button,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import SubtleBadge from "components/common/subtleBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import axios from "axios"; // Import axios to make API requests
import "assets/css/site.css";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  fetchRoleAndClaims,
  handleLoadingAndAccess,
} from "../../assets/js/common";
import { useAppContext } from "../../providers/appProvider";
import moment from "moment";
import { getDomainConfig } from "../../config/domainConfig";
import { ROLE_NAMES } from "../../config/roleConfig";
import { normalizePhoneForDisplay } from "./../../utils/phoneNormalizer";

const UserDetails = () => {
  const locationData = useLocation();
  const { t, i18n } = useTranslation();
  const userIdRaw = locationData.pathname.split("/")[2];
  const userId= userIdRaw ? userIdRaw.toUpperCase() : null;
  const [roleId, setRoleId] = useState("");
  const [data, setData] = useState([]); 
  const [claimData, setClaimData] = useState([]);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const handleClose = () => setShowModal(false);
  const [loading, setLoading] = useState(true); 
  const navigate = useNavigate(); 
  const [role, setRole] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [locationName, setLocationName] = useState(null); 
  const [allLocations, setAllLocations] = useState(null); 
  const { config } = useAppContext();
  const isAppOwner = localStorage.getItem("UserRole") === ROLE_NAMES.APP_OWNER;
  const isAdmin = localStorage.getItem("UserRole") === ROLE_NAMES.ADMIN;
  const { financialLabels: finLabels } = getDomainConfig();

  const fetchAllLocations = async (customerId) => {
    if (!customerId) return [];

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/locationRoute/all`,
      {
        params: {
          customerId: customerId,
        },
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret,
        },
      }
    );

    return response.data;
  };

  const fetchUserData = async (userId) => {
    setLoading(true);
    try {
      // const locationId = config.location.LocationId||localStorage.getItem("LocationId"); // Get locationId from sessionStorage

      // if (!locationId) {
      //   console.error("LocationId is missing");
      //   return;
      // }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      setRoleId(response.data.Role);
      setData(response.data);
      setCustomerId(response.data.CustomerId);

      const locations = response.data.locations;

      if (Array.isArray(locations) && locations.length > 0) {
        const allLocations = await fetchAllLocations(response.data.CustomerId);

        const locationNames = locations
          .map(userLoc =>
            allLocations.find(
              loc => loc.LocationId === userLoc.LocationId
            )?.Name
          )
          .filter(Boolean)
          .join(", ");

        setLocationName(locationNames || null);
      } else {
        setLocationName(null);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      const performedBy = localStorage.getItem("UserId");
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/deleteUser/${userId}`,
        { IsDelete: true, performedBy },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      if (response.status === 200) {
        toast.success(t("userDeleted"), {
          position: "top-right",
          autoClose: 5000,
        });
        return true;
      }
    } catch (error) {
      toast.error(t("formError"), {
        position: "top-right",
        autoClose: 5000,
      });
      console.error(
        "Error deleting user:",
        error.response ? error.response.data : error.message
      );
    }

    return false;
  };

  const deactivateUser = async (userId) => {
    try {
      const inactiveTime = moment().toISOString();
      const performedBy = localStorage.getItem("UserId");
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/userRoute/deactive/${userId}`,
        { InactiveDateTime: inactiveTime, performedBy },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      if (response.status === 200) {
        toast.success(t("userDeactivate"), {
          position: "top-right",
          autoClose: 5000,
        });
        return true;
      }
    } catch (error) {
      toast.error(t("formError"), {
        position: "top-right",
        autoClose: 5000,
      });
      console.error(
        "Error deactivating user:",
        error.response ? error.response.data : error.message
      );
    }

    return false;
  };

  const fetchRole = async (roleId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/roleRoute/getRoleById/${roleId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setRole(response.data);
    } catch (err) {
      setError("Failed to fetch locations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const StaticBackdropModal = ({ show, handleClose, userId }) => {
    const handleDelete = async () => {
      const success = await deleteUser(userId);
      if (success) {
        navigate("/users");
      } else {
        console.error("Failed to delete the user");
      }
      handleClose();
    };

    const handleDeactivateUser = async () => {
      const success = await deactivateUser(userId);
      if (success) {
        navigate("/users");
      } else {
        console.error("Failed to deactivate the user");
      }
      handleClose();
    };

    return (
      <>
        {/* Delete User Modal */}
        <Modal show={show} onHide={handleClose} backdrop="static">
          <Modal.Header>
            <Modal.Title>{t("confirmation")}</Modal.Title>
            <Button variant="close" onClick={handleClose} />
          </Modal.Header>
          <Modal.Body>{t("deleteUserConfirmation")}</Modal.Body>
          <Modal.Footer>
            <Button variant="falcon-default" onClick={handleClose}>
              {t("cancel")}
            </Button>
            <Button variant="primary" onClick={handleDelete}>
              {t("delete")}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Deactivate User Modal */}
        <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t("deactivateUser")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{t("deactivateUserConfirmation")}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="falcon-default" onClick={() => setShowDeactivateModal(false)}>
              {t("cancel")}
            </Button>
            <Button variant="primary" onClick={handleDeactivateUser}>
              {t("deactivate")}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  };

  const fetchClaims = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/claimRoute/claims`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      // Check if response.data is an array
      if (!Array.isArray(response.data)) {
        console.error("Expected an array but got:", response.data);
        return;
      }
      //Use optional chaining to avoid undefined errors
      const filteredClaims = response.data.filter(
        (claim) => claim?.RoleId === roleId
      );
      setClaimData(filteredClaims);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching claims data:", error);
      setLoading(false);
    }
  };

  const handleEditUserDetails = (userId) => {
    navigate(`${paths.editUser.replace(":userId", userId.toLowerCase())}`);
  };

  const roleTranslations = {
    [ROLE_NAMES.APP_OWNER]: t("appOwnerText"),
    [ROLE_NAMES.ADMIN]: t("adminText"),
    [ROLE_NAMES.CLIENT]: t("client"),
    [ROLE_NAMES.EMPLOYEE]: t("employeeText"),
  };

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "users",
      claimValue: "view",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  useEffect(() => {
    fetchUserData(userId);
  }, [userId, config.location]);

  useEffect(() => {
    if (roleId) {
      // Ensure roleId is defined before calling fetchRole
      fetchRole(roleId);
      fetchClaims();
    }
  }, [roleId]);

  const hasClaim = (claimType, claimValues) => {
    return claimData.some(
      (claim) => claim.Type === claimType && claimValues.includes(claim.Value)
    );
  };

  //  Generate GUID token
  const generateToken = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = (Math.random() * 16) | 0;
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  };

  // --- Helper to get user details ---
  const getUserDetails = async (userId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`,
      { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
    );
    return response.data;
  };

  // --- Helper to create a notification ---
  const createNotification = async ({ message, raisedFrom, receivedTo, sourceId }) => {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/notificationsRoute/notification/create`,
      {
        message,
        raisedFrom,
        receivedTo,
        type: "In-App",
        isRead: false,
        isActionRequired: false,
        DateTime: moment.utc().format(),
        sourceId: sourceId,
        sourceType: "user",
        sourceDate: null,
        eventKey: "PASSWORD_RESET_BY_ADMIN",
      },
      { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
    );
  };

  // --- Main function to create reset password notification ---
  const createResetPasswordNotification = async ({ userId }) => {
    try {
      const adminIdFromStorage = localStorage.getItem("UserId");

      // Fetch both admin and user details
      const [adminData, userData] = await Promise.all([
        getUserDetails(adminIdFromStorage),
        getUserDetails(userId),
      ]);

      const adminName = adminData?.Name;
      const userName = userData?.Name;

      // Message (using translation)
      const messageForUser = t("in-AppNotifications.passwordResetByAdmin", {
        userName,
        adminName,
      });

      //  Notification to the user (not to admin)
      await createNotification({
        message: messageForUser,
        raisedFrom: adminIdFromStorage, // who triggered it
        receivedTo: userId, // user who receives it
        sourceId: userId,
      });
    } catch (err) {
      console.error("Failed to create reset password notification:", err);
    }
  };

  // --- Reset Password Logic ---
  const resetPassword = async (response, userData, data, paths) => {
    try {
      const createdUserId = response.data.UserId;
      const culture = localStorage.getItem("culture") || "nl";
      const token = generateToken();
      const resetLink = `${window.location.origin}${paths.resetPassword}?token=${token}&culture=${culture}`;
      const userEmail = userData.Emailaddress;
      const loggedInUserId = localStorage.getItem("UserId")

      // Save token in backend
      await axios.post(
        `${process.env.REACT_APP_API_URL}/tokenDataRoute/createToken`,
        { token, userId: createdUserId, tokenUsed: false },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      // Send reset email to user
      await axios.post(
        `${process.env.REACT_APP_API_URL}/emailRoute/sendResetPasswordEmail`,
        {
          email: userEmail,
          name: userData.Name,
          resetLink,
          culture,
          customerId: userData.CustomerId,
          loggedInUserId: loggedInUserId
        },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      // Send only user notification (no admin notification)
      await createResetPasswordNotification({ userId: createdUserId });
    } catch (error) {
      console.error("Error in resetPassword:", error.response || error);
      throw error;
    }
  };

  // --- Handler Function ---
  const handleResetPassword = async () => {
    if (!data?.UserId) {
      toast.error(t("userNotLoaded"), { position: "top-right", autoClose: 4000 });
      return;
    }

    try {
      setIsLoading(true);

      const resetPayload = { data: { UserId: data.UserId } };
      await resetPassword(resetPayload, data, null, paths);

      toast.success(t("resetPasswordEmailSent"), { position: "top-right" });
    } catch (err) {
      console.error("handleResetPassword error:", err);
      toast.error(t("resetPasswordFailed"), { position: "top-right", autoClose: 6000 });
    } finally {
      setIsLoading(false);
    }
  };

  const loadingOrRedirect = handleLoadingAndAccess(isLoading, hasAccess);

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background image={corner4} className="bg-card" />
            <Card.Body className="position-relative pe-3 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("details")}</h5>
                  <Breadcrumb>
                    <Breadcrumb.Item
                      as={Link}
                      to="/users"
                      className="fw-semi-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("users")}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      className="fw-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {data.Name ? data.Name : " "}
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loading || !customerId ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </div>
      ) : (
        <>
          <Card className="mb-3">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0 text-black">{t("userDetails")}</h5>
                </Col>
                <Col xs={12} md="auto" className="mt-2 mt-md-0">
                  <div className="d-flex flex-wrap gap-2 justify-content-start">

                    {(isAppOwner || isAdmin) && data?.Status === "0" && (
                      <IconButton
                        className="btn-sm"
                        variant="info"
                        icon="key"
                        onClick={handleResetPassword}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          t("resetPasswordLabel")
                        )}
                      </IconButton>
                    )}

                    <IconButton
                      className="btn-sm"
                      variant="primary"
                      icon="pencil-alt"
                      onClick={() => handleEditUserDetails(userId)}
                    >
                      {t("edit")}
                    </IconButton>

                    <span title={data?.Status !== "0" ? t("alreadyDeactivated") : ""}>
                      <IconButton
                        className="btn-sm"
                        variant="warning"
                        icon="ban"
                        onClick={() => setShowDeactivateModal(true)}
                        disabled={data?.Status !== "0"}
                      >
                        {t("deactivate")}
                      </IconButton>
                    </span>

                    <IconButton
                      className="btn-sm"
                      variant="danger"
                      icon="trash"
                      onClick={() => setShowModal(true)}
                    >
                      {t("delete")}
                    </IconButton>
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="bg-light border-top">
              <Row>
                <Col lg xxl={6}>
                  <h6 className="fw-semi-bold ls mb-3 data-font-colour data-font-size">
                    {t("personalDetails")}
                  </h6>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("name")}
                      </p>
                    </Col>
                    <Col>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {data.Name}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("userNumber")}
                      </p>
                    </Col>
                    <Col>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {data.UserNumber}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("phoneNumber")}
                      </p>
                    </Col>
                    <Col>
                        <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                          {normalizePhoneForDisplay(data.PhoneNumber, "NL") || "--"}
                        </p>
                    </Col>
                  </Row>
                </Col>

                <Col lg xxl={6}>
                  <h6 className="fw-semi-bold ls mb-3 data-font-colour">
                    {t("accountInformation")}
                  </h6>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("emailAddress")}
                      </p>
                    </Col>
                    <Col>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {data.Emailaddress}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("roleText")}
                      </p>
                    </Col>
                    <Col>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {roleTranslations[role?.Name] || "Onbekend"}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("manager")}
                      </p>
                    </Col>
                    <Col>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {data.manager?.Name || "--"}
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="mt-4 mt-lg-3">
<Col lg={6} xxl={6} className="mt-4 mt-lg-2">
                  <h6 className="fw-semi-bold ls mb-3 data-font-colour">
                    {t("employmentDetails")}
                  </h6>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("speciality")}
                      </p>
                    </Col>
                    <Col>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {Array.isArray(data.specialities) && data.specialities.length > 0
                          ? data.specialities
                              .map((s) => s.speciality?.Name && t(s.speciality.Name))
                              .filter(Boolean)
                              .join(", ")
                          : "--"}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("employmentDate")}
                      </p>
                    </Col>
                    <Col>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {data.DateOfJoining
                          ? moment(data.DateOfJoining).format("DD-MM-YYYY")
                          : "--"}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("status")}
                      </p>
                    </Col>
                    <Col>
                      <SubtleBadge
                        pill
                        bg={data.Status === "0" ? "success" : "warning"}
                        className="d-inline-flex align-items-center px-2 mb-1"
                      >
                        <p className="mb-0 data-font-size">
                          {data.Status === "0" ? t("active") : t("deactive")}
                        </p>
                        <FontAwesomeIcon
                          icon={data.Status === "0" ? "check" : "ban"}
                          transform="shrink-2"
                          className="ms-1"
                        />
                      </SubtleBadge>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("employmentType")}
                      </p>
                    </Col>
                    <Col>
                      {data.EmploymentType ? (
                        <SubtleBadge
                          pill
                          bg={data.EmploymentType === "permanent" ? "success" : "info"}
                          className="d-inline-flex align-items-center px-2 mb-1"
                        >
                          <p className="mb-0 data-font-size">
                            {data.EmploymentType === "permanent" ? t("permanent") : t("contract")}
                          </p>
                        </SubtleBadge>
                      ) : (
                        <p className="fw-semi-bold mb-1 data-font-colour data-font-size">--</p>
                      )}
                    </Col>
                  </Row>

                  {data.EmploymentType === "contract" && (
                    <>
                      <Row>
                        <Col xs={6} sm={5}>
                          <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                            {t("contractStartDate")}
                          </p>
                        </Col>
                        <Col>
                          <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                            {data.ContractStartDate
                              ? moment(data.ContractStartDate).format("DD-MM-YYYY")
                              : "--"}
                          </p>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} sm={5}>
                          <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                            {t("contractEndDate")}
                          </p>
                        </Col>
                        <Col>
                          <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                            {data.ContractEndDate
                              ? moment(data.ContractEndDate).format("DD-MM-YYYY")
                              : "--"}
                          </p>
                        </Col>
                      </Row>
                      {data.ContractNotes && (
                        <Row>
                          <Col xs={6} sm={5}>
                            <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                              {t("contractNotes")}
                            </p>
                          </Col>
                          <Col>
                            <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                              {data.ContractNotes}
                            </p>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </Col>

                <Col lg={6} xxl={6} className="mt-4 mt-lg-2">
                  <h6 className="fw-semi-bold ls mb-3 data-font-colour">
                    {t("branchDetails")}
                  </h6>
                  <Row>
                    <Col xs={6} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("branchName")}
                      </p>
                    </Col>
                    <Col>
                      {locationName ? (
                        <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                          {locationName}
                        </p>
                      ) : (
                        "--"
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="mt-4 mt-lg-3 row">
                <div className="mt-4 mt-lg-2 col-xxl-6 col-lg-6">
                  <h6 className="fw-semi-bold ls mb-3 data-font-colour">
                    {t("administration")}
                  </h6>

                  {/* Vasturen */}
                  <Row className="mb-2">
                    <Col xs={5} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("fixedHours")}
                      </p>
                    </Col>
                    <Col>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {data.FixedHours != null
                          ? data.FixedHours.toString().replace(".", ",")
                          : "--"}
                      </p>
                    </Col>
                  </Row>

                  {data.EmploymentType === "contract" && (
                    <Row className="mb-2">
                      <Col xs={5} sm={5}>
                        <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                          {t("hourlyRate")}
                        </p>
                      </Col>
                      <Col>
                        <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                          {data.HourlyRate != null
                            ? `${finLabels.currencySymbol} ${data.HourlyRate.toString().replace(".", ",")}`
                            : "--"}
                        </p>
                      </Col>
                    </Row>
                  )}

                  {data.EmploymentType === "permanent" && (
                    <Row className="mb-2">
                      <Col xs={5} sm={5}>
                        <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                          {t("salary")}
                        </p>
                      </Col>
                      <Col>
                        <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                          {data.Salary != null
                            ? `${finLabels.currencySymbol} ${data.Salary.toString().replace(".", ",")}${
                                data.SalaryPeriod
                                  ? ` / ${data.SalaryPeriod === "monthly" ? t("salaryPerMonth") : t("salaryPerWeek")}`
                                  : ""
                              }`
                            : "--"}
                        </p>
                      </Col>
                    </Row>
                  )}

                  {/* Weekdays */}
                  <Row>
                    <Col xs={5} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("weekdays")}
                      </p>
                    </Col>

                    <Col>
                      {data.Weekdays ? (
                        (() => {
                          let weekdays = [];

                          try {
                            weekdays = JSON.parse(data.Weekdays); // ["monday","tuesday"]
                          } catch {
                            return "--";
                          }

                          return weekdays.length ? (
                            weekdays.map(day => (
                              <SubtleBadge
                                key={day}
                                pill
                                bg="success"
                                className="me-1 mb-1 text-capitalize d-inline-flex flex-center data-font-size"
                              >
                                <p className="mb-0">{t(`days.${day}`)}</p>
                              </SubtleBadge>
                            ))
                          ) : (
                            "--"
                          );
                        })()
                      ) : (
                        "--"
                      )}
                    </Col>
                  </Row>

                </div>
              </Row>
            </Card.Body>
          </Card>
          <Card className="mb-3">
            <Card.Header>
              <h5 className="mb-0 data-font-colour">{t("accessLevel")}</h5>
            </Card.Header>
            <Card.Body className="border-top">
              <Table bordered={true} responsive size="sm">
                <thead>
                  <tr>
                    <th rowSpan="2">
                      <h6 className="fw-semi-bold ls data-font-colour data-font-size">
                        {t("categories")}
                      </h6>
                    </th>
                    <th colSpan="4" className="text-center">
                      <h6 className="fw-semi-bold ls data-font-colour data-font-size">
                        {t("permissions")}
                      </h6>
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <h6 className="fw-semi-bold ls data-font-colour data-font-size">
                        {t("view")}
                      </h6>
                    </th>
                    <th>
                      <h6 className="fw-semi-bold ls data-font-colour data-font-size">
                        {t("create")}
                      </h6>
                    </th>
                    <th>
                      <h6 className="fw-semi-bold ls data-font-colour data-font-size">
                        {t("edit")}
                      </h6>
                    </th>
                    <th>
                      <h6 className="fw-semi-bold ls data-font-colour data-font-size">
                        {t("delete")}
                      </h6>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Relaties */}
                  <tr>
                    <td>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("relations")}
                      </p>
                    </td>
                    <td>
                      {hasClaim("relations", ["view"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("relations", ["create"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("relations", ["edit"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("relations", ["delete"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                  </tr>

                  {/* Offertes */}
                  <tr>
                    <td>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("proposals")}
                      </p>
                    </td>
                    <td>
                      {hasClaim("proposal", ["view"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("proposal", ["create"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("proposal", ["edit"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("proposal", ["delete"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                  </tr>

                  {/* Administratie */}
                  <tr>
                    <td>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("settings")}
                      </p>
                    </td>
                    <td>
                      {hasClaim("settings", ["view"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("settings", ["create"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("settings", ["edit"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("settings", ["delete"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                  </tr>

                  {/* Gebruikers */}
                  <tr>
                    <td>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                        {t("users")}
                      </p>
                    </td>
                    <td>
                      {hasClaim("users", ["view"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("users", ["create"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("users", ["edit"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                    <td>
                      {hasClaim("users", ["delete"]) ? (
                        <FontAwesomeIcon
                          icon="check"
                          className="text-success"
                          size="sm"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon="times"
                          className="text-danger"
                          size="sm"
                        />
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}
      <StaticBackdropModal
        show={showModal}
        handleClose={handleClose}
        userId={userId}
      />
    </>
  );
};

export default UserDetails;